import { type IFolder, isFolder, isProject } from '@store/api/folderEndpoints';
import { type IProject } from '@store/api/projectEndpoints';

interface ListedItem {
  id: string;
  name: string;
  dateCreated: string;
  dateModified: string;
  /** A project's file size in bytes; folders have none. */
  size?: number;
  /** How many folders and projects a folder holds, shown where a project shows its size. */
  itemCount?: number;
  /**
   * A team's root folder. It is managed through the Teams page; the folder API rejects
   * edit/delete on it for every role, admins included.
   */
  isTeamRoot?: boolean;
  /** A folder another team shared, surfaced at root level for the target team's members. */
  isSharedWithMe?: boolean;
}

export type CustomFolderChildRow = ListedItem & ({ type: 'Folder', item: IFolder } | { type: 'Project', item: IProject });

/** A folder's own folders, as the listing and the tree show them alike. */
export const folderRowsOf = (folder: IFolder | undefined): CustomFolderChildRow[] =>
  folder?.children.filter(isFolder).map((child) => ({
    id: child.item._id,
    name: child.item.name,
    dateCreated: child.item.createdAt,
    dateModified: child.item.updatedAt,
    itemCount: child.item.children.length,
    isTeamRoot: Boolean(child.item.teamId),
    isSharedWithMe: Boolean(child.item.isSharedWithMe),
    ...child,
  })) ?? [];

/** A project as a row, whether a folder lists it or a search found it. */
export const projectRowOf = (project: IProject): CustomFolderChildRow => ({
  type: 'Project',
  item: project,
  id: project.projectInfo.id,
  name: project.projectInfo.name,
  dateCreated: project.projectInfo.dateCreated,
  dateModified: project.projectInfo.dateModified,
  size: project.size,
});

/** A folder's own projects, as the listing and the tree show them alike. */
export const projectRowsOf = (folder: IFolder | undefined): CustomFolderChildRow[] =>
  folder?.children.filter(isProject).map((child) => projectRowOf(child.item)) ?? [];

/** Whether the folder at `path` is the one at `ancestor` or below it, by whole segments. */
export const isSameOrBelow = (path: string, ancestor: string) =>
  path === ancestor || path.startsWith(ancestor === '/' ? '/' : `${ancestor}/`);
